import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useInstitution } from '../../context/InstitutionContext';
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc
} from 'firebase/firestore';
import { db } from '../../firebaseconf';
import { getStudentsByInstitution } from '../../utils/firebaseQueries';

export default function ManageStudents() {
  const { currentUser } = useAuth();
  const { currentInstitution } = useInstitution();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [filterBranch, setFilterBranch] = useState('all');
  const [branches, setBranches] = useState([]);
  const [classes, setClasses] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    profileId: '',
    branch: '',
    status: 'active'
  });

  // Extract branches from students
  const extractBranchesFromStudents = (studentsList) => {
    const branchSet = new Set();
    studentsList.forEach(student => {
      if (student.profileId) {
        const parts = student.profileId.split('_');
        if (parts.length >= 2) {
          branchSet.add(parts[1].toUpperCase());
        }
      }
    });
    return Array.from(branchSet);
  };

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const instId = currentInstitution?.id || currentInstitution?.institutionId;
      if (!instId) {
        setError('Institution not found');
        setLoading(false);
        return;
      }

      // Use the firebase queries utility to get students
      const studentsList = await getStudentsByInstitution(instId);
      setStudents(studentsList);
      
      // Extract unique branches
      const branchList = extractBranchesFromStudents(studentsList);
      setBranches(branchList);
      
      // Fetch classes from the institution
      try {
        const classesCollection = collection(db, 'institutions', instId, 'classes');
        const classesSnapshot = await getDocs(classesCollection);
        const classList = classesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClasses(classList);
      } catch (err) {
        console.log('Classes not yet available:', err);
        setClasses([]); // Use empty array if classes don't exist yet
      }
      
      setError('');
    } catch (err) {
      console.error('Error fetching students:', err);
      setError('Failed to fetch students. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentInstitution?.id, currentInstitution?.institutionId]);

  useEffect(() => {
    if (currentInstitution?.id || currentInstitution?.institutionId) {
      fetchStudents();
    }
  }, [currentInstitution?.id, currentInstitution?.institutionId, fetchStudents]);

  const handleEdit = (student) => {
    setSelectedStudent(student);
    
    // Extract branch from profileId
    const extractBranch = (profileId) => {
      if (!profileId) return '';
      const parts = profileId.split('_');
      return parts.length >= 2 ? parts[1].toUpperCase() : '';
    };
    
    setFormData({
      name: student.name || '',
      email: student.email || '',
      phone: student.phone || '',
      profileId: student.profileId || '',
      branch: extractBranch(student.profileId) || '',
      status: student.status || 'active'
    });
    setOpenDialog(true);
  };

  const handleDeleteClick = (student) => {
    setSelectedStudent(student);
    setOpenDeleteDialog(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveStudent = async () => {
    try {
      if (!formData.name) {
        setError('Name is required');
        return;
      }

      const studentRef = doc(db, 'users', selectedStudent.uid);
      
      await updateDoc(studentRef, {
        name: formData.name,
        phone: formData.phone,
        status: formData.status,
        updatedAt: new Date(),
        updatedBy: currentUser.email
      });

      setSuccess('Student updated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      handleCloseDialog();
      fetchStudents();
    } catch (err) {
      console.error('Error updating student:', err);
      setError('Failed to update student. Please try again.');
    }
  };

  const handleDeleteStudent = async () => {
    try {
      // In a real system, you might want to set status to inactive instead of deleting
      const studentRef = doc(db, 'users', selectedStudent.uid);
      
      await updateDoc(studentRef, {
        status: 'inactive'
      });

      setSuccess('Student deactivated successfully!');
      setTimeout(() => setSuccess(''), 3000);
      handleCloseDeleteDialog();
      fetchStudents();
    } catch (err) {
      console.error('Error deleting student:', err);
      setError('Failed to deactivate student. Please try again.');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStudent(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      profileId: '',
      branch: '',
      status: 'active'
    });
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedStudent(null);
  };

  // Extract branch from profileId
  const extractBranch = (profileId) => {
    if (!profileId) return 'General';
    const parts = profileId.split('_');
    return parts.length >= 2 ? parts[1].toUpperCase() : 'General';
  };

  const filteredStudents = students.filter(student => {
    let match = true;
    if (filterBranch !== 'all') {
      const studentBranch = extractBranch(student.profileId);
      match = studentBranch === filterBranch;
    }
    return match;
  });

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <h2>Manage Students</h2>
          <p style={{ color: '#666', margin: 0 }}>
            Total: {students.length} | Active: {students.filter(s => s.status === 'active').length}
          </p>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => alert('Import CSV feature coming soon')}
          >
            Import CSV
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            href="/studentform"
          >
            Add Student
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <FormControl sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Branch</InputLabel>
          <Select
            value={filterBranch}
            label="Filter by Branch"
            onChange={(e) => setFilterBranch(e.target.value)}
          >
            <MenuItem value="all">All Branches</MenuItem>
            {branches.map(branch => (
              <MenuItem key={branch} value={branch}>
                {branch}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Phone</strong></TableCell>
              <TableCell><strong>Branch</strong></TableCell>
              <TableCell><strong>Profile ID</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredStudents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <p style={{ color: '#999' }}>No students found. <a href="/studentform">Add one now</a></p>
                </TableCell>
              </TableRow>
            ) : (
              filteredStudents.map(student => (
                <TableRow key={student.id} hover>
                  <TableCell>{student.name}</TableCell>
                  <TableCell>{student.rollNumber}</TableCell>
                  <TableCell>{student.email || '-'}</TableCell>
                  <TableCell>{student.class || '-'}</TableCell>
                  <TableCell>{student.semester || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={student.status || 'active'}
                      color={student.status === 'active' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEdit(student)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(student)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Student Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Student</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              label="Roll Number"
              name="rollNumber"
              value={formData.rollNumber}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              label="Registration Number"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              label="Phone"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
            />
            <FormControl fullWidth>
              <InputLabel>Branch</InputLabel>
              <Select
                name="branch"
                value={formData.branch}
                label="Branch"
                onChange={handleInputChange}
              >
                <MenuItem value="">Select Branch</MenuItem>
                {branches.map(branch => (
                  <MenuItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Class</InputLabel>
              <Select
                name="class"
                value={formData.class}
                label="Class"
                onChange={handleInputChange}
              >
                <MenuItem value="">Select Class</MenuItem>
                {classes.map(cls => (
                  <MenuItem key={cls.id} value={cls.id}>
                    {cls.className || cls.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Semester"
              name="semester"
              value={formData.semester}
              onChange={handleInputChange}
              placeholder="e.g., 1, 2, 3"
            />
            <TextField
              fullWidth
              label="Section"
              name="section"
              value={formData.section}
              onChange={handleInputChange}
              placeholder="e.g., A, B, C"
            />
            <TextField
              fullWidth
              label="Date of Birth"
              name="dateOfBirth"
              type="date"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              label="Father's Name"
              name="fatherName"
              value={formData.fatherName}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              label="Mother's Name"
              name="motherName"
              value={formData.motherName}
              onChange={handleInputChange}
            />
            <TextField
              fullWidth
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              multiline
              rows={2}
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={formData.status}
                label="Status"
                onChange={handleInputChange}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="graduated">Graduated</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveStudent} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Student</DialogTitle>
        <DialogContent>
          <p>
            Are you sure you want to delete <strong>{selectedStudent?.name}</strong>?
          </p>
          <p style={{ color: '#d32f2f', fontSize: '0.9rem' }}>
            This action cannot be undone. All student records will be deleted.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDeleteStudent} variant="contained" color="error">
            Delete Student
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
